import { useState } from 'react'
import { Globe } from 'lucide-react'
import type { MerchantBrief, MerchantIdentity } from '../../api/merchant'
import { Icon } from '../icons/Icon'
import { CategoryIcon } from '../icons/CategoryIcon'
import { CatalogueIconImg } from '../icons/CatalogueIconImg'
import { BY_SLUG } from '../icons/categoryIconPaths'
import './merchantAvatar.css'

export type { MerchantBrief, MerchantIdentity }

/**
 * A merchant's visual identity — its logo when the server has one, a colour
 * swatch with an initial when it does not.
 *
 * **`MerchantIdentity`-shaped — a `MerchantBrief` widened by `icon_url`.**
 * `TransactionResponse` embeds exactly `name`, `type`, `icon`, `color`,
 * `logo_url` and `is_global`, and no `icon_url`; `MerchantResponse` carries
 * the URL too. Taking the widened brief means a transaction row passes what
 * it already has and a merchants row passes a whole response, both without a
 * mapping step — see `src/api/merchant.ts` for the full reasoning. This
 * component lives in `src/platform/primitives/` rather than under
 * `src/routes/merchants/` so M6 (Transactions) can render the same identity
 * inline, with no import into a sibling route.
 *
 * **The type is imported from `src/api/merchant.ts`, never from
 * `src/api/generated/schema` directly — Task 2 review, finding 4.** No
 * `src/platform/**` module may depend on the generated client: `CategoryChip`,
 * the component the brief named as this one's model, takes flat props for
 * exactly that reason. `src/api/merchant.ts` is the one place the alias is
 * declared, importable from both a platform primitive and a route's hook, so
 * a backend rename is a single compile error rather than two independently
 * drifting copies.
 *
 * **Four steps, not three — #461.** Logo, then the catalogue's `icon_url`,
 * then the bundled pack for a legacy slug the design set still knows, then the
 * name's initial. The catalogue step is new because `POST`/`PATCH /merchants`
 * now validate `icon` against the backend catalogue and answer with the URL
 * they resolved; nothing here composes one.
 *
 * **The merchant's colour arrives through `--swatch`, an inline custom
 * property carrying server-supplied colour (the category surfaces used the
 * same mechanism until #454 removed category colour) — set once, on the outer element, and consumed by a
 * `border` on whichever of the logo or the swatch actually renders.**
 * `merchantAvatar.css` carries the full reasoning for why `border` and not
 * `box-shadow` or `outline`, why the fill stays `--chip` rather than a tint of
 * the merchant colour, and why that safety is conditional on this component
 * never becoming a `<button>`/`<a href>`.
 *
 * **The global marker is an `Icon`, not a hue.** M0-R11: colour is never the
 * only signal, and a `title` alone is invisible to a keyboard user who never
 * hovers. `Icon`'s own `label` prop turns the glyph into `role="img"` with an
 * `aria-label`, which is what `getByLabelText` below finds — no second
 * aria-label span invented for the same job `Icon` already does.
 *
 * **`broken` resets when `logo_url` changes, via React's adjust-state-during-
 * render pattern rather than `useEffect`.** Task 2 review, finding 3: a prior
 * version left `broken` latched for the life of the component instance,
 * reasoning that a *different* merchant re-keys the whole component the way
 * `ListRow` callers do (`key={child.id}`). That covers a different merchant,
 * not the SAME merchant's URL changing under an unchanged key — Task 9's
 * `POST /merchants/{id}/logo` replacing a previously-broken logo, or a
 * transient offline blip (this app ships an offline banner, so the state is
 * real) latching the fallback past reconnection. Comparing during render
 * rather than in an effect avoids painting one stale frame with the OLD URL's
 * `broken` flag before the effect would fire.
 */
export function MerchantAvatar({
  merchant,
  size = 'row',
}: {
  merchant: MerchantIdentity
  size?: 'row' | 'detail'
}) {
  const [broken, setBroken] = useState(false)
  const [trackedUrl, setTrackedUrl] = useState(merchant.logo_url)
  if (merchant.logo_url !== trackedUrl) {
    setTrackedUrl(merchant.logo_url)
    setBroken(false)
  }
  const showLogo = merchant.logo_url != null && !broken

  // `[...name][0]`, not `name.charAt(0)` — Task 2 review, minor. `charAt`
  // indexes UTF-16 code units, so a name starting with an emoji or an
  // astral-plane character splits its surrogate pair and renders a
  // replacement character. Spreading a string iterates by Unicode code
  // point instead, at the same cost.
  //
  // Empty stays empty rather than becoming an accidental space, so `?? '?'`
  // would not do here — `[...''][0]` is `undefined`, which is falsy, and `||`
  // is exactly the operator that wants. A name that is ONLY punctuation
  // renders that punctuation uppercased, which is real data; an EMPTY name
  // renders as a rendering fault would, `?`, rather than an empty circle
  // nobody can tell apart from a bug.
  const initial = ([...merchant.name.trim()][0] ?? '?').toUpperCase()

  const badgeIconSize = size === 'detail' ? 12 : 8
  const iconSize = size === 'detail' ? 22 : 15
  /* `undefined` for both "no icon set" and "a slug this pack does not know",
     which the render below treats identically — the letter. */
  const glyph = merchant.icon == null ? undefined : BY_SLUG[merchant.icon]
  const fallbackGlyph =
    glyph !== undefined ? (
      <CategoryIcon category={{ name: merchant.name, icon: merchant.icon }} size={iconSize} />
    ) : (
      <span className="merchant-avatar__glyph">{initial}</span>
    )

  return (
    <span
      className={`merchant-avatar merchant-avatar--${size}`}
      data-testid="merchant-avatar"
      // ALWAYS set, never conditionally — `CategoryForm.tsx` documents why:
      // the stylesheet reads a bare `var(--swatch)`, because the composite
      // walker refuses a nested `var(..., var(..))` fallback. The neutral
      // default is a token STRING supplied from here, not a CSS fallback. Set
      // once here rather than on each branch below, since both the logo and
      // the swatch consume the same `border`.
      style={{ '--swatch': merchant.color ?? 'var(--chip)' } as React.CSSProperties}
    >
      {showLogo ? (
        <img
          className="merchant-avatar__logo"
          src={merchant.logo_url!}
          alt={merchant.name}
          onError={() => setBroken(true)}
          // M11-R25: "Merchant logos load lazily; a long list on a phone
          // connection must not block on images." This is the only place in
          // the module that requirement can live.
          loading="lazy"
          decoding="async"
          // The app URL — which will carry merchant ids — is sent to a
          // third-party host (Plaid's CDN or this app's own GCS bucket) on
          // every logo load. The standard hedge against leaking it via the
          // Referer header.
          referrerPolicy="no-referrer"
        />
      ) : (
        // `aria-hidden` — Task 2 review, finding C. A caller rendering this
        // beside the merchant's own name (M6's row, this component's first
        // real caller) would otherwise announce "Costco, C" — the fallback
        // letter is decorative, the same call `CategoryChip` makes for its
        // dot, not new information a screen-reader user needs.
        <span className="merchant-avatar__swatch" aria-hidden="true">
          {/* The user's chosen glyph if there is one, the name's initial if
              not — a FOUR-step fallback since #461: logo, then the
              catalogue's own `icon_url`, then the bundled pack, then the
              letter.
              
              This is what makes the picker mean anything: only Plaid's global
              merchants carry a logo, so for the merchants a user creates
              themselves the icon IS the identity. Reading it was recorded as
              "gated on #286" long after #286 closed.
              
              `BY_SLUG` guards an unknown value: `icon` is a free string on the
              wire (max 50), so a slug this build's pack does not carry — an
              older icon set, or a value written by another client — falls back
              to the letter rather than rendering an empty box. */}
          {/* The catalogue SVG the SERVER resolved (`icon_url`) — never a URL
              composed here — else the bundled pack's glyph, else the letter.
              `CatalogueIconImg` owns the empty-URL check, the dark-theme
              filter and the failed-load fallthrough to `fallbackGlyph`.
              Decorative (`alt=""`): the swatch is `aria-hidden` and the
              caller renders the merchant's name as text. */}
          <CatalogueIconImg
            src={merchant.icon_url}
            size={iconSize}
            className="merchant-avatar__icon"
            fallback={fallbackGlyph}
          />
        </span>
      )}
      {merchant.is_global && (
        <span className="merchant-avatar__badge">
          <Icon glyph={Globe} label="Shared merchant" size={badgeIconSize} />
        </span>
      )}
    </span>
  )
}
